import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Latin to Kazakh Cyrillic transliteration mapping
const latinToKazakh: Record<string, string> = {
  // Basic letters
  'a': 'а', 'b': 'б', 'c': 'ц', 'd': 'д', 'e': 'е', 'f': 'ф', 'g': 'г', 'h': 'х',
  'i': 'и', 'j': 'ж', 'k': 'к', 'l': 'л', 'm': 'м', 'n': 'н', 'o': 'о', 'p': 'п',
  'q': 'қ', 'r': 'р', 's': 'с', 't': 'т', 'u': 'ұ', 'v': 'в', 'w': 'у', 'x': 'х',
  'y': 'ы', 'z': 'з',

  // Uppercase letters
  'A': 'А', 'B': 'Б', 'C': 'Ц', 'D': 'Д', 'E': 'Е', 'F': 'Ф', 'G': 'Г', 'H': 'Х',
  'I': 'И', 'J': 'Ж', 'K': 'К', 'L': 'Л', 'M': 'М', 'N': 'Н', 'O': 'О', 'P': 'П',
  'Q': 'Қ', 'R': 'Р', 'S': 'С', 'T': 'Т', 'U': 'Ұ', 'V': 'В', 'W': 'У', 'X': 'Х',
  'Y': 'Ы', 'Z': 'З',

  // Special combinations (must be processed first)
  'ch': 'ч', 'sh': 'ш', 'zh': 'ж', 'ng': 'ң', 'gh': 'ғ',
  'Ch': 'Ч', 'Sh': 'Ш', 'Zh': 'Ж', 'Ng': 'Ң', 'Gh': 'Ғ',
  'CH': 'Ч', 'SH': 'Ш', 'ZH': 'Ж', 'NG': 'Ң', 'GH': 'Ғ',
};

export function transliterateToKazakh(text: string): string {
  let result = text;

  // Handle apostrophe-based Kazakh letters first
  const apostropheMappings = [
    { latin: "a'", kazakh: 'ә' },
    { latin: "A'", kazakh: 'Ә' },
    { latin: "o'", kazakh: 'ө' },
    { latin: "O'", kazakh: 'Ө' },
    { latin: "u'", kazakh: 'ү' },
    { latin: "U'", kazakh: 'Ү' },
    { latin: "i'", kazakh: 'й' },
    { latin: "I'", kazakh: 'Й' },
    { latin: "y'", kazakh: 'і' },
    { latin: "Y'", kazakh: 'І' },
    { latin: "g'", kazakh: 'ғ' },
    { latin: "G'", kazakh: 'Ғ' },
  ];

  // Replace apostrophe-based mappings
  for (const mapping of apostropheMappings) {
    result = result.replace(new RegExp(mapping.latin.replace("'", "\\'"), 'g'), mapping.kazakh);
  }

  // Handle special combinations (longer sequences)
  const specialCombinations = [
    { latin: 'ch', kazakh: 'ч' },
    { latin: 'sh', kazakh: 'ш' },
    { latin: 'zh', kazakh: 'ж' },
    { latin: 'ng', kazakh: 'ң' },
    { latin: 'gh', kazakh: 'ғ' },
    { latin: 'Ch', kazakh: 'Ч' },
    { latin: 'Sh', kazakh: 'Ш' },
    { latin: 'Zh', kazakh: 'Ж' },
    { latin: 'Ng', kazakh: 'Ң' },
    { latin: 'Gh', kazakh: 'Ғ' },
    { latin: 'CH', kazakh: 'Ч' },
    { latin: 'SH', kazakh: 'Ш' },
    { latin: 'ZH', kazakh: 'Ж' },
    { latin: 'NG', kazakh: 'Ң' },
    { latin: 'GH', kazakh: 'Ғ' },
  ];

  // Replace special combinations
  for (const combo of specialCombinations) {
    result = result.replace(new RegExp(combo.latin, 'g'), combo.kazakh);
  }

  // Replace individual characters
  for (const [latin, kazakh] of Object.entries(latinToKazakh)) {
    result = result.replace(new RegExp(latin, 'g'), kazakh);
  }

  return result;
}

// Cyrillic to Latin transliteration (reverse)
export function transliterateToLatin(text: string): string {
  let result = text;

  // Handle special Cyrillic letters (Kazakh-specific)
  const kazakhSpecificMappings = [
    { cyrillic: 'ә', latin: "ä" },
    { cyrillic: 'Ә', latin: "Ä" },
    { cyrillic: 'ө', latin: "ö" },
    { cyrillic: 'Ө', latin: "Ö" },
    { cyrillic: 'ү', latin: "ü" },
    { cyrillic: 'Ү', latin: "Ü" },
    { cyrillic: 'ұ', latin: 'ū' },
    { cyrillic: 'Ұ', latin: 'Ū' },
    { cyrillic: 'қ', latin: 'q' },
    { cyrillic: 'Қ', latin: 'Q' },
    { cyrillic: 'ғ', latin: "ğ" },
    { cyrillic: 'Ғ', latin: "Ğ" },
    { cyrillic: 'һ', latin: 'h' },
    { cyrillic: 'Һ', latin: 'H' },
    { cyrillic: 'і', latin: "ı" },
    { cyrillic: 'І', latin: "I" },
  ];

  // Replace Kazakh-specific letters first
  for (const mapping of kazakhSpecificMappings) {
    result = result.split(mapping.cyrillic).join(mapping.latin);
  }

  // Handle multi-character Cyrillic combinations
  const specialCombinations = [
    { cyrillic: 'ч', latin: 'ch' },
    { cyrillic: 'Ч', latin: 'Ç' },
    { cyrillic: 'ш', latin: 'ş' },
    { cyrillic: 'Ш', latin: 'Ş' },
    { cyrillic: 'ң', latin: 'ñ' },
    { cyrillic: 'Ң', latin: 'Ñ' },
  ];

  for (const combo of specialCombinations) {
    result = result.split(combo.cyrillic).join(combo.latin);
  }

  // Handle regular Cyrillic letters
  const cyrillicToLatin: Record<string, string> = {
    'а': 'a',
    'б': 'b',
    'в': 'v',
    'г': 'g',
    'д': 'd',
    'е': 'e',
    'ё': 'yo',
    'ж': 'j',
    'з': 'z',
    'и': 'i',
    'й': "i",
    'к': 'k',
    'л': 'l',
    'м': 'm',
    'н': 'n',
    'о': 'o',
    'п': 'p',
    'р': 'r',
    'с': 's',
    'т': 't',
    'у': 'u',
    'ф': 'f',
    'х': 'h',
    'ц': 'ts',
    'ы': 'y',
    'э': 'e',
    'ю': 'yu',
    'я': 'ya',
    'А': 'A',
    'Б': 'B',
    'В': 'V',
    'Г': 'G',
    'Д': 'D',
    'Е': 'E',
    'Ё': 'Yo',
    'Ж': 'J',
    'З': 'Z',
    'И': 'İ',
    'Й': "İ",
    'К': 'K',
    'Л': 'L',
    'М': 'M',
    'Н': 'N',
    'О': 'O',
    'П': 'P',
    'Р': 'R',
    'С': 'S',
    'Т': 'T',
    'У': 'U',
    'Ф': 'F',
    'Х': 'H',
    'Ц': 'TS',
    'Ы': 'Y',
    'Э': 'E',
    'Ю': 'Yu',
    'Я': 'Ya',
  };

  // Replace regular Cyrillic letters
  for (const [cyrillic, latin] of Object.entries(cyrillicToLatin)) {
    result = result.split(cyrillic).join(latin);
  }

  return result;
}

// English Latin to Kazakh Latin transliteration
export function transliterateEnglishToKazakhLatin(text: string): string {
  let result = text;

  // Handle apostrophe-based notation for Kazakh-specific letters
  const apostropheMappings = [
    { pattern: "a'", replacement: 'ä' },
    { pattern: "A'", replacement: 'Ä' },
    { pattern: "o'", replacement: 'ö' },
    { pattern: "O'", replacement: 'Ö' },
    { pattern: "u'", replacement: 'ü' },
    { pattern: "U'", replacement: 'Ü' },
    { pattern: "i'", replacement: 'i' }, // й sound but keep as i in Latin
    { pattern: "I'", replacement: 'İ' },
    { pattern: "y'", replacement: 'ı' }, // і sound
    { pattern: "Y'", replacement: 'I' },
    { pattern: "g'", replacement: 'ğ' },
    { pattern: "G'", replacement: 'Ğ' },
  ];

  // Replace apostrophe-based mappings first
  for (const mapping of apostropheMappings) {
    result = result.replace(new RegExp(mapping.pattern.replace("'", "\\'"), 'g'), mapping.replacement);
  }

  // Handle special digraphs and trigraphs (process longer patterns first)
  const specialCombinations = [
    // Lowercase
    { pattern: 'sh', replacement: 'ş' },
    { pattern: 'ch', replacement: 'ç' },
    { pattern: 'ng', replacement: 'ñ' },
    { pattern: 'gh', replacement: 'ğ' },
    { pattern: 'zh', replacement: 'j' },
    // Title case
    { pattern: 'Sh', replacement: 'Ş' },
    { pattern: 'Ch', replacement: 'Ç' },
    { pattern: 'Ng', replacement: 'Ñ' },
    { pattern: 'Gh', replacement: 'Ğ' },
    { pattern: 'Zh', replacement: 'J' },
    // Uppercase
    { pattern: 'SH', replacement: 'Ş' },
    { pattern: 'CH', replacement: 'Ç' },
    { pattern: 'NG', replacement: 'Ñ' },
    { pattern: 'GH', replacement: 'Ğ' },
    { pattern: 'ZH', replacement: 'J' },
  ];

  // Replace special combinations
  for (const combo of specialCombinations) {
    result = result.replace(new RegExp(combo.pattern, 'g'), combo.replacement);
  }

  // Handle macron notation for ū (u with macron)
  result = result.replace(/u-/g, 'ū');
  result = result.replace(/U-/g, 'Ū');

  // Also support common alternative notations
  result = result.replace(/uu/g, 'ū'); // double u can represent ū
  result = result.replace(/UU/g, 'Ū');
  result = result.replace(/Uu/g, 'Ū');

  return result;
}

// Russian to Kazakh Cyrillic transliteration
export function transliterateRussianToKazakh(text: string): string {
  let result = text;

  // Most Cyrillic letters are shared between Russian and Kazakh
  // This function handles phonetic adjustments for Russian-specific letters

  // Russian letters that don't exist in Kazakh alphabet need conversion
  const russianToKazakhMappings = [
    // Soft/hard signs are typically omitted in Kazakh
    { russian: 'ь', kazakh: '' },
    { russian: 'Ь', kazakh: '' },
    { russian: 'ъ', kazakh: '' },
    { russian: 'Ъ', kazakh: '' },

    // Russian 'щ' can be represented as 'ш' in Kazakh
    { russian: 'щ', kazakh: 'ш' },
    { russian: 'Щ', kazakh: 'Ш' },

    // Russian 'э' typically becomes 'е' in Kazakh
    { russian: 'э', kazakh: 'е' },
    { russian: 'Э', kazakh: 'Е' },

    // Russian 'ё' typically becomes 'ё' or 'е' in Kazakh (keeping as 'ё')
    // Russian 'ы' exists in Kazakh, keep as is
    // Russian 'й' exists in Kazakh, keep as is
  ];

  // Apply Russian-specific letter conversions
  for (const mapping of russianToKazakhMappings) {
    result = result.split(mapping.russian).join(mapping.kazakh);
  }

  // Note: Russian letters я, ю, ё remain as they are used in Kazakh for Russian loanwords
  // Common Cyrillic letters (а, б, в, г, д, е, ж, з, и, к, л, м, н, о, п, р, с, т, у, ф, х, ц, ч, ш, ы)
  // are identical in both alphabets and don't need conversion

  return result;
}
